import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI, parsePaginated } from '../services/api';
import Pagination, { PAGE_SIZE } from './Pagination';

function SalonOwners() {
  // Sample data - in a real app, this would come from an API
  const [salonOwners, setSalonOwners] = useState([]);

  // State for search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [activeTab, setActiveTab] = useState('pending'); // Default to pending tab
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  // New state for edit modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingOwner, setEditingOwner] = useState(null);
  
  // New state for confirmation modal
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState('');
  const [ownerToUpdate, setOwnerToUpdate] = useState(null);
  
  // New state for premium subscription modal
  const [isPremiumModalOpen, setIsPremiumModalOpen] = useState(false);
  const [premiumOwner, setPremiumOwner] = useState(null);

  // Add these state variables at the top of your component
  const [isEmployeeModalOpen, setIsEmployeeModalOpen] = useState(false);
  const [selectedSalonEmployees, setSelectedSalonEmployees] = useState([]);
  const [selectedSalonName, setSelectedSalonName] = useState('');

  // Add to your existing state variables
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [approvalData, setApprovalData] = useState({
    salon_id: null,
    approve: true,
    membership_plan: 'premium',
    amount_paid: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    payment_method: 'UPI'
  });

  const mapPendingOwner = (owner) => ({
    id: owner.id || owner.salon_id,
    name: owner.owner_name || 'Unknown',
    salonName: owner.salon_name || 'Unknown Salon',
    location: owner.location || 'Unknown Location',
    status: 'Pending',
    joinDate: owner.registered_on ? new Date(owner.registered_on).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    employees: owner.employees || 0,
    email: owner.email || '',
    contactNo: owner.contact_no || '',
    uniqueId: owner.unique_id || 'Not assigned',
    subscriptionType: 'None',
    premiumStatus: 'Inactive',
    premiumStartDate: '',
    premiumEndDate: ''
  });

  const mapActiveOwner = (owner) => ({
    id: owner.id,
    name: owner.owner_name,
    salonName: owner.salon_name,
    location: owner.location,
    status: owner.is_approved ? 'Active' : 'Inactive',
    joinDate: owner.registered_on ? new Date(owner.registered_on).toISOString().split('T')[0] : '',
    employees: 0,
    subscriptionType: owner.membership_plan || 'None',
    premiumStatus: owner.membership_active ? 'Active' : 'Inactive',
    premiumStartDate: owner.membership_start_date || '',
    premiumEndDate: owner.membership_end_date || '',
    email: owner.email,
    contactNo: owner.contact_no,
    uniqueId: owner.unique_id || 'Not assigned'
  });

  const fetchPendingApprovals = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await adminAPI.getPendingApprovals({
        page: currentPage,
        pageSize: PAGE_SIZE,
        search: debouncedSearch,
        status: 'pending',
      });
      const parsed = parsePaginated(response.data);
      setSalonOwners(parsed.results.map(mapPendingOwner));
      setTotalCount(parsed.count);
      setPendingCount(parsed.count);
      if (parsed.results.length === 0 && parsed.count > 0 && currentPage > 1) {
        setCurrentPage((page) => Math.max(1, page - 1));
      }
    } catch (err) {
      setError('Failed to fetch pending approvals');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, debouncedSearch]);

  const fetchActiveSalonOwners = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await adminAPI.getSalonOwners({
        page: currentPage,
        pageSize: PAGE_SIZE,
        search: debouncedSearch,
        is_approved: true,
      });
      const parsed = parsePaginated(response.data);
      setSalonOwners(parsed.results.map(mapActiveOwner));
      setTotalCount(parsed.count);
      if (parsed.results.length === 0 && parsed.count > 0 && currentPage > 1) {
        setCurrentPage((page) => Math.max(1, page - 1));
      }
    } catch (err) {
      setError('Failed to fetch salon owners');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, debouncedSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const next = searchTerm.trim();
      setDebouncedSearch((prev) => {
        if (prev !== next) {
          setCurrentPage(1);
        }
        return next;
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'pending') {
      fetchPendingApprovals();
    } else if (activeTab === 'active') {
      fetchActiveSalonOwners();
    }
  }, [activeTab, fetchPendingApprovals, fetchActiveSalonOwners]);

  // Handle owner selection for details view
  const handleViewDetails = (owner) => {
    setSelectedOwner(owner);
    setIsModalOpen(true);
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedOwner(null);
  };
  
  // Handle edit button click
  const handleEditClick = (owner) => {
    setEditingOwner({...owner});
    setIsEditModalOpen(true);
  };
  
  // Save edited owner
  const handleSaveEdit = () => {
    setSalonOwners(salonOwners.map(owner => 
      owner.id === editingOwner.id ? editingOwner : owner
    ));
    setIsEditModalOpen(false);
    setEditingOwner(null);
  };
  
  // Handle status change confirmation
  const handleStatusChangeClick = (owner) => {
    setOwnerToUpdate(owner);
    const isActivating = owner.status !== 'Active';
    setConfirmAction(isActivating ? 'activate' : 'deactivate');
    
    // If activating, set up the approval data with required fields
    if (isActivating) {
      const oneYearLater = new Date(new Date().setFullYear(new Date().getFullYear() + 1));
      setApprovalData({
        salon_id: owner.id,
        approve: true,
        membership_plan: 'premium',
        amount_paid: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: oneYearLater.toISOString().split('T')[0],
        payment_method: 'Bank Transfer'
      });
    }
    
    setIsConfirmModalOpen(true);
  };
  
  // Confirm status change
  const confirmStatusChange = async () => {
    // Validate required fields for approval or activation
    if ((confirmAction === 'approve' || confirmAction === 'activate') && !approvalData.amount_paid) {
      setError('Amount paid is required');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      let statusData;
      
      if (confirmAction === 'approve') {
        // For approval, use the approvalData directly
        statusData = {
          is_approved: true,
          membership_plan: approvalData.membership_plan,
          membership_active: true,
          membership_amount_paid: parseFloat(approvalData.amount_paid) || 0,
          membership_start_date: approvalData.start_date,
          membership_end_date: approvalData.end_date,
          membership_payment_method: approvalData.payment_method
        };
        
        await adminAPI.approveSalon(approvalData);
      } else if (confirmAction === 'activate') {
        // For activation, set is_approved to true and include required membership details
        statusData = {
          is_approved: true,
          membership_plan: 'premium',
          membership_active: true,
          membership_amount_paid: parseFloat(approvalData.amount_paid) || 2000.0,
          membership_start_date: approvalData.start_date || new Date().toISOString().split('T')[0],
          membership_end_date: approvalData.end_date || new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
          membership_payment_method: approvalData.payment_method || "Bank Transfer"
        };
        
        await adminAPI.updateSalonStatus(ownerToUpdate.id, statusData);
      } else if (confirmAction === 'deactivate') {
        // For deactivation, clear membership details
        statusData = {
          is_approved: false,
          membership_plan: "",
          membership_active: false,
          membership_amount_paid: 0,
          membership_start_date: "",
          membership_end_date: "",
          membership_payment_method: ""
        };
        
        await adminAPI.updateSalonStatus(ownerToUpdate.id, statusData);
      } else {
        // For other status changes, use the existing logic
        statusData = {
          is_approved: confirmAction !== 'deactivate',
          membership_plan: ownerToUpdate.subscriptionType || 'basic',
          membership_active: ownerToUpdate.premiumStatus === 'Active',
          membership_amount_paid: parseFloat(approvalData.amount_paid) || 0,
          membership_start_date: ownerToUpdate.premiumStartDate || new Date().toISOString().split('T')[0],
          membership_end_date: ownerToUpdate.premiumEndDate || new Date().toISOString().split('T')[0],
          membership_payment_method: approvalData.payment_method || "Bank Transfer"
        };
        
        await adminAPI.updateSalonStatus(ownerToUpdate.id, statusData);
      }
      
      // Refresh the list after status change
      if (activeTab === 'pending') {
        fetchPendingApprovals();
      } else {
        fetchActiveSalonOwners();
      }
      
      setIsConfirmModalOpen(false);
      setOwnerToUpdate(null);
    } catch (err) {
      setError('Failed to update salon status');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle approve salon owner
  const handleApproveOwner = (owner) => {
    setApprovalData({
      ...approvalData,
      salon_id: owner.id,
      approve: true,
      membership_active: true, // Add this line to explicitly set premium as active
      start_date: new Date().toISOString().split('T')[0],
      end_date: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0]
    });
    setOwnerToUpdate(owner);
    setConfirmAction('approve');
    setIsConfirmModalOpen(true);
  };

  // Handle reject salon owner
  const handleRejectOwner = (owner) => {
    setApprovalData({
      ...approvalData,
      salon_id: owner.id,
      approve: false
    });
    setOwnerToUpdate(owner);
    setConfirmAction('reject');
    setIsConfirmModalOpen(true);
  };

  // Handle premium subscription management
  const handlePremiumClick = (owner) => {
    setPremiumOwner({
      ...owner,
      newPremiumStatus: owner.premiumStatus || 'Inactive',
      newPremiumStartDate: owner.premiumStartDate || new Date().toISOString().split('T')[0],
      newPremiumEndDate: owner.premiumEndDate || new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
      amount_paid: '',
      payment_method: 'Bank Transfer'
    });
    setIsPremiumModalOpen(true);
  };
  
  // Save premium subscription changes
  const handleSavePremium = async () => {
    setIsLoading(true);
    try {
      const premiumData = {
        is_approved: true,
        membership_plan: 'premium',
        membership_active: premiumOwner.newPremiumStatus === 'Active',
        membership_amount_paid: parseFloat(premiumOwner.amount_paid) || 15000,
        membership_start_date: premiumOwner.newPremiumStartDate,
        membership_end_date: premiumOwner.newPremiumEndDate,
        membership_payment_method: premiumOwner.payment_method || "Bank Transfer"
      };
      
      await adminAPI.updateSalonStatus(premiumOwner.id, premiumData);
      
      // Refresh the list after premium update
      fetchActiveSalonOwners();
      
      setIsPremiumModalOpen(false);
      setPremiumOwner(null);
    } catch (err) {
      setError('Failed to update premium status');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Toggle premium status
  const togglePremiumStatus = (id) => {
    setSalonOwners(salonOwners.map(owner => 
      owner.id === id ? {
        ...owner, 
        premiumStatus: owner.premiumStatus === 'Active' ? 'Inactive' : 'Active'
      } : owner
    ));
  };

  // Add this function to handle employee click
  const handleEmployeeClick = (owner) => {
    // Generate random join dates within the last 3 years
    const generateRandomJoinDate = () => {
      const today = new Date();
      const threeYearsAgo = new Date();
      threeYearsAgo.setFullYear(today.getFullYear() - 3);
      
      const randomTimestamp = threeYearsAgo.getTime() + Math.random() * (today.getTime() - threeYearsAgo.getTime());
      const randomDate = new Date(randomTimestamp);
      
      return randomDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
    };

    // In a real application, you would fetch employees from an API
    const mockEmployees = [
      { 
        id: 1, 
        name: `${owner.name}'s Employee 1`, 
        position: 'Hair Stylist', 
        joinDate: generateRandomJoinDate(), 
        contact: '123-456-7890' 
      },
      { 
        id: 2, 
        name: `${owner.name}'s Employee 2`, 
        position: 'Makeup Artist', 
        joinDate: generateRandomJoinDate(), 
        contact: '123-456-7891' 
      },
      { 
        id: 3, 
        name: `${owner.name}'s Employee 3`, 
        position: 'Nail Technician', 
        joinDate: generateRandomJoinDate(), 
        contact: '123-456-7892' 
      },
    ].slice(0, owner.employees); // Only show as many employees as the owner has

    setSelectedSalonEmployees(mockEmployees);
    setSelectedSalonName(owner.salonName);
    setIsEmployeeModalOpen(true);
  };

  // Add this function to close the employee modal
  const closeEmployeeModal = () => {
    setIsEmployeeModalOpen(false);
    setSelectedSalonEmployees([]);
    setSelectedSalonName('');
  };

  // Helper functions
  const calculateDuration = (dateString) => {
    const now = new Date();
    const joinDate = new Date(dateString);
    const diffTime = Math.abs(now - joinDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const years = Math.floor(diffDays / 365);
    const months = Math.floor((diffDays % 365) / 30);
    const days = diffDays % 30;

    if (years > 0) {
      return `${years} year${years > 1 ? 's' : ''}`;
    } else if (months > 0) {
      return `${months} month${months > 1 ? 's' : ''}`;
    } else {
      return `${days} day${days > 1 ? 's' : ''}`;
    }
  };

  const calculateRemainingDays = (endDateString) => {
    if (!endDateString) return 'N/A';
    
    const now = new Date();
    const endDate = new Date(endDateString);
    
    // If end date is in the past, return "Expired"
    if (endDate < now) {
      return "Expired";
    }
    
    const diffTime = Math.abs(endDate - now);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    const years = Math.floor(diffDays / 365);
    const months = Math.floor((diffDays % 365) / 30);
    const days = diffDays % 30;
    
    let result = '';
    if (years > 0) {
      result += `${years} year${years > 1 ? 's' : ''} `;
    }
    if (months > 0) {
      result += `${months} month${months > 1 ? 's' : ''} `;
    }
    if (days > 0 || (years === 0 && months === 0)) {
      result += `${days} day${days > 1 ? 's' : ''}`;
    }
    
    return `${result.trim()} remaining`;
  };

  // Calculate premium period between two dates
  const calculatePremiumPeriod = (startDate, endDate) => {
    if (!startDate || !endDate) return 'N/A';
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 'N/A';
    
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    const years = Math.floor(diffDays / 365);
    const months = Math.floor((diffDays % 365) / 30);
    const days = diffDays % 30;
    
    let result = '';
    if (years > 0) {
      result += `${years} year${years > 1 ? 's' : ''} `;
    }
    if (months > 0) {
      result += `${months} month${months > 1 ? 's' : ''} `;
    }
    if (days > 0 || (years === 0 && months === 0)) {
      result += `${days} day${days > 1 ? 's' : ''}`;
    }
    
    return result.trim();
  };

  // Add this function to check and update expired premium memberships
  const checkAndUpdateExpiredMemberships = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      let page = 1;
      let loaded = 0;
      let total = Infinity;

      while (loaded < total) {
        const response = await adminAPI.getSalonOwners({ page, pageSize: 100 });
        const parsed = parsePaginated(response.data);
        total = parsed.count;
        loaded += parsed.results.length;
        if (parsed.results.length === 0) break;

        for (const owner of parsed.results) {
          if (owner.membership_active && owner.membership_end_date && owner.membership_end_date < today) {
            const statusData = {
              is_approved: false,
              membership_plan: "",
              membership_active: false,
              membership_start_date: "",
              membership_end_date: "",
              membership_amount_paid: 0,
              membership_payment_method: ""
            };
            await adminAPI.updateSalonStatus(owner.id, statusData);
          }
        }
        page += 1;
      }
    } catch (err) {
      console.error('Failed to check expired memberships:', err);
    }
  };

  // Call this function when component mounts
  useEffect(() => {
    checkAndUpdateExpiredMemberships();
    // Set interval to check daily (86400000 ms = 24 hours)
    const intervalId = setInterval(checkAndUpdateExpiredMemberships, 86400000);
    
    return () => clearInterval(intervalId);
  }, []);

  return (
    <div className="container mx-auto px-4 py-6">
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">Salon Owners</h1>
      </div>

      {/* Tab Navigation - Modern Design */}
      <div className="mb-6">
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="flex">
            <button
              onClick={() => {
                setSearchTerm('');
                setCurrentPage(1);
                setActiveTab('pending');
              }}
              className={`flex-1 py-4 px-6 text-center relative transition-all duration-200 ${
                activeTab === 'pending'
                  ? 'text-primary-600 font-medium'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Pending Approval</span>
                {pendingCount > 0 && (
                  <span className="ml-1 bg-yellow-100 text-yellow-800 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                    {pendingCount}
                  </span>
                )}
              </div>
              {activeTab === 'pending' && <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary-500"></div>}
            </button>
            <button
              onClick={() => {
                setSearchTerm('');
                setCurrentPage(1);
                setActiveTab('active');
              }}
              className={`flex-1 py-4 px-6 text-center relative transition-all duration-200 ${
                activeTab === 'active'
                  ? 'text-primary-600 font-medium'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Active Salon Owners</span>
              </div>
              {activeTab === 'active' && <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary-500"></div>}
            </button>
          </div>
        </div>
      </div>

      {/* Pending Approvals Section - Only show on pending tab */}
      {activeTab === 'pending' && (
        <div className="bg-white shadow rounded-lg overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Pending Approval Requests</h2>
            <p className="text-sm text-gray-500 mb-4">Review and approve new salon owner registrations</p>
            <input
              type="text"
              className="block w-full md:w-1/2 py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-600 focus:border-primary-600"
              placeholder="Search by name, salon, unique ID, or location"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          {isLoading ? (
            <div className="flex justify-center py-8">
              <svg className="animate-spin h-8 w-8 text-primary-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Owner Name</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Salon Name</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unique ID</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Join Date</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {salonOwners.length > 0 ? (
                    salonOwners.map((owner) => (
                      <tr key={owner.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-yellow-100 rounded-full flex items-center justify-center">
                              <span className="text-yellow-600 font-medium">{owner.name.charAt(0)}</span>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{owner.name}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {owner.salonName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className="px-2 py-1 bg-gray-100 rounded-md font-mono">{owner.uniqueId}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{owner.contactNo}</div>
                          <div className="text-sm text-gray-500">{owner.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{owner.location}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{owner.joinDate}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button 
                              onClick={() => handleViewDetails(owner)}
                              className="text-primary-600 hover:text-primary-900"
                            >
                              View
                            </button>
                            <button 
                              onClick={() => handleApproveOwner(owner)}
                              className="text-green-600 hover:text-green-900"
                            >
                              Approve
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                        No pending approval requests at this time.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          <div className="px-4 pb-4">
            <Pagination
              page={currentPage}
              total={totalCount}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      )}

      {/* Search and Filters - Only show on active tab */}
      {activeTab === 'active' && (
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  id="search"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-600 focus:border-primary-600"
                  placeholder="Search by name, salon, unique ID, or location"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Salon Owners Table - Only show on active tab */}
      {activeTab === 'active' && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">All Salon Owners</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Salon Owner</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unique ID</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Join Date</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account Status</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Premium Status</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Premium Period</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {salonOwners.length > 0 ? (
                  salonOwners.map((owner) => (
                    <tr key={owner.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                            <span className="text-primary-600 font-medium">{owner.name.charAt(0)}</span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{owner.name}</div>
                            <div className="text-sm text-gray-500">{owner.salonName}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className="px-2 py-1 bg-gray-100 rounded-md font-mono">{owner.uniqueId}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{owner.location}</td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{owner.joinDate}</div>
                        <div className="text-xs text-gray-500">{calculateDuration(owner.joinDate)} ago</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          owner.status === 'Active' ? 'bg-green-100 text-green-800' : 
                          owner.status === 'Inactive' ? 'bg-red-100 text-red-800' : 
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {owner.status === 'Pending' ? 'Pending Approval' : owner.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {owner.premiumStatus ? (
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            owner.premiumStatus === 'Active' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {owner.premiumStatus}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-500">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {owner.premiumStartDate && owner.premiumEndDate ? (
                          <div>
                            <div>{owner.premiumStartDate} to {owner.premiumEndDate}</div>
                            <div className="text-xs text-gray-500">
                              {calculateRemainingDays(owner.premiumEndDate)}
                            </div>
                          </div>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button 
                            onClick={() => handleViewDetails(owner)}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            View
                          </button>
                          <button 
                            onClick={() => handleStatusChangeClick(owner)}
                            className={owner.status === 'Active' ? "text-red-600 hover:text-red-900" : "text-green-600 hover:text-green-900"}
                          >
                            {owner.status === 'Active' ? 'Deactivate' : 'Activate'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                      No salon owners found matching your filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="px-4 pb-4">
            <Pagination
              page={currentPage}
              total={totalCount}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      )}

      {/* Details Modal */}
      {isModalOpen && selectedOwner && (
        <div className="fixed inset-0 overflow-y-auto z-50">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={closeModal}></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-xl leading-6 font-bold text-gray-900">
                        Salon Owner Details
                      </h3>
                      <button 
                        onClick={closeModal}
                        className="text-gray-400 hover:text-gray-500"
                      >
                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    
                    <div className="bg-gray-50 p-4 rounded-lg mb-6">
                      <div className="flex items-center mb-4">
                        <div className="h-16 w-16 bg-primary-100 rounded-full flex items-center justify-center">
                          <span className="text-primary-600 text-xl font-medium">{selectedOwner.name.charAt(0)}</span>
                        </div>
                        <div className="ml-4">
                          <h4 className="text-lg font-medium text-gray-900">{selectedOwner.name}</h4>
                          <p className="text-sm text-gray-500">Joined on {selectedOwner.joinDate} ({calculateDuration(selectedOwner.joinDate)} ago)</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div>
                          <h5 className="text-sm font-medium text-gray-500">Salon Name</h5>
                          <p className="text-sm font-medium text-gray-900">{selectedOwner.salonName}</p>
                        </div>
                        <div>
                          <h5 className="text-sm font-medium text-gray-500">Unique ID</h5>
                          <p className="text-sm font-medium text-gray-900 font-mono bg-gray-100 px-2 py-1 rounded inline-block">
                            {selectedOwner.uniqueId}
                          </p>
                        </div>
                        <div>
                          <h5 className="text-sm font-medium text-gray-500">Salon Number</h5>
                          <p className="text-sm font-medium text-gray-900">{selectedOwner.contactNo}</p>
                        </div>
                        <div>
                          <h5 className="text-sm font-medium text-gray-500">Location</h5>
                          <p className="text-sm font-medium text-gray-900">{selectedOwner.location}</p>
                        </div>
                        <div>
                          <h5 className="text-sm font-medium text-gray-500">Status</h5>
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            selectedOwner.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {selectedOwner.status}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mb-6">
                      <h4 className="text-lg font-medium text-gray-900 mb-3">Premium Subscription Details</h4>
                      <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                        <div className="flex items-center mb-3">
                          <div className="h-8 w-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                            <svg className="h-5 w-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                            </svg>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-900">Subscription Type:</span>
                            <span className="ml-2 px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800">
                              {selectedOwner.subscriptionType}
                            </span>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <h5 className="text-sm font-medium text-gray-500">Premium Status</h5>
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              selectedOwner.premiumStatus === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {selectedOwner.premiumStatus || 'Not Active'}
                            </span>
                          </div>
                          <div>
                            <h5 className="text-sm font-medium text-gray-500">Start Date</h5>
                            <p className="text-sm font-medium text-gray-900">{selectedOwner.premiumStartDate || 'N/A'}</p>
                          </div>
                          <div>
                            <h5 className="text-sm font-medium text-gray-500">End Date</h5>
                            <p className="text-sm font-medium text-gray-900">{selectedOwner.premiumEndDate || 'N/A'}</p>
                          </div>
                          <div>
                            <h5 className="text-sm font-medium text-gray-500">Premium Period</h5>
                            <p className="text-sm font-medium text-gray-900">
                              {selectedOwner.premiumStartDate && selectedOwner.premiumEndDate ? 
                                calculatePremiumPeriod(selectedOwner.premiumStartDate, selectedOwner.premiumEndDate) : 'N/A'}
                            </p>
                          </div>
                          <div>
                            <h5 className="text-sm font-medium text-gray-500">Remaining Time</h5>
                            <p className="text-sm font-medium text-gray-900">
                              {selectedOwner.premiumEndDate ? calculateRemainingDays(selectedOwner.premiumEndDate) : 'N/A'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={closeModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Edit Modal */}
      {isEditModalOpen && editingOwner && (
        <div className="fixed inset-0 overflow-y-auto z-50">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={() => setIsEditModalOpen(false)}></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Edit Salon Owner
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Owner Name
                    </label>
                    <input
                      type="text"
                      value={editingOwner.name}
                      onChange={(e) => setEditingOwner({...editingOwner, name: e.target.value})}
                      className="w-full border border-gray-300 rounded-md shadow-sm p-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Salon Name
                    </label>
                    <input
                      type="text"
                      value={editingOwner.salonName}
                      onChange={(e) => setEditingOwner({...editingOwner, salonName: e.target.value})}
                      className="w-full border border-gray-300 rounded-md shadow-sm p-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Location
                    </label>
                    <input
                      type="text"
                      value={editingOwner.location}
                      onChange={(e) => setEditingOwner({...editingOwner, location: e.target.value})}
                      className="w-full border border-gray-300 rounded-md shadow-sm p-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Employees
                    </label>
                    <input
                      type="number"
                      value={editingOwner.employees}
                      onChange={(e) => setEditingOwner({...editingOwner, employees: parseInt(e.target.value) || 0})}
                      className="w-full border border-gray-300 rounded-md shadow-sm p-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      value={editingOwner.status}
                      onChange={(e) => setEditingOwner({...editingOwner, status: e.target.value})}
                      className="w-full border border-gray-300 rounded-md shadow-sm p-2"
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Subscription Type
                    </label>
                    <select
                      value={editingOwner.subscriptionType}
                      onChange={(e) => setEditingOwner({...editingOwner, subscriptionType: e.target.value})}
                      className="w-full border border-gray-300 rounded-md shadow-sm p-2"
                    >
                      <option value="Premium">Premium</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleSaveEdit}
                >
                  Save Changes
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setIsEditModalOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Confirmation Modal */}
      {isConfirmModalOpen && ownerToUpdate && (
        <div className="fixed inset-0 overflow-y-auto z-50">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={() => setIsConfirmModalOpen(false)}></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full sm:mx-0 sm:h-10 sm:w-10 
                    ${confirmAction === 'deactivate' || confirmAction === 'reject' ? 'bg-red-100' : 'bg-green-100'}">
                    {confirmAction === 'deactivate' || confirmAction === 'reject' ? (
                      <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    ) : (
                      <svg className="h-6 w-6 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    {confirmAction === 'approve' || confirmAction === 'activate' ? (
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">
                          {confirmAction === 'approve' ? 'Approve Salon Owner' : 'Activate Salon Owner'}
                        </h3>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Membership Plan</label>
                            <select
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                              value={approvalData.membership_plan}
                              onChange={(e) => setApprovalData({...approvalData, membership_plan: e.target.value})}
                            >
                              <option value="premium">Premium</option>
                            </select>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Amount Paid <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                              value={approvalData.amount_paid}
                              onChange={(e) => setApprovalData({...approvalData, amount_paid: e.target.value})}
                              placeholder="Enter amount"
                              required
                            />
                            {!approvalData.amount_paid && (
                              <p className="mt-1 text-sm text-red-500">Amount is required</p>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Start Date</label>
                              <input
                                type="date"
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                                value={approvalData.start_date}
                                onChange={(e) => setApprovalData({...approvalData, start_date: e.target.value})}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">End Date</label>
                              <input
                                type="date"
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                                value={approvalData.end_date}
                                onChange={(e) => setApprovalData({...approvalData, end_date: e.target.value})}
                              />
                            </div>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Payment Method</label>
                            <select
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                              value={approvalData.payment_method}
                              onChange={(e) => setApprovalData({...approvalData, payment_method: e.target.value})}
                            >
                              <option value="UPI">UPI</option>
                              <option value="Bank Transfer">Bank Transfer</option>
                              <option value="Credit Card">Credit Card</option>
                              <option value="Cash">Cash</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    ) : confirmAction === 'reject' ? (
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Reject Salon Owner</h3>
                        <p className="text-sm text-gray-500 mt-2">
                          Are you sure you want to reject {ownerToUpdate?.name}'s account request? 
                          Their registration will be declined.
                        </p>
                      </div>
                    ) : (
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Deactivate Salon Owner</h3>
                        <p className="text-sm text-gray-500 mt-2">
                          Are you sure you want to deactivate {ownerToUpdate?.name}'s account? 
                          They will no longer be able to access the system.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 ${
                    confirmAction === 'deactivate' || confirmAction === 'reject' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
                  } text-base font-medium text-white focus:outline-none sm:ml-3 sm:w-auto sm:text-sm`}
                  onClick={confirmStatusChange}
                  disabled={(confirmAction === 'approve' || confirmAction === 'activate') && !approvalData.amount_paid}
                >
                  {confirmAction === 'deactivate' ? 'Deactivate' : 
                   confirmAction === 'activate' ? 'Activate' :
                   confirmAction === 'approve' ? 'Approve' : 'Reject'}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setIsConfirmModalOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Premium Subscription Modal */}
      {isPremiumModalOpen && premiumOwner && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Premium Subscription</h3>
              <button 
                onClick={() => setIsPremiumModalOpen(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500 mb-2">Salon Owner: <span className="font-medium text-gray-900">{premiumOwner.name}</span></p>
                <p className="text-sm text-gray-500 mb-2">Salon: <span className="font-medium text-gray-900">{premiumOwner.salonName}</span></p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Premium Status</label>
                <select
                  className="block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-600 focus:border-primary-600"
                  value={premiumOwner.newPremiumStatus || premiumOwner.premiumStatus || 'Inactive'}
                  onChange={(e) => setPremiumOwner({...premiumOwner, newPremiumStatus: e.target.value})}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Premium Start Date</label>
                  <input
                    type="date"
                    className="block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-600 focus:border-primary-600"
                    value={premiumOwner.newPremiumStartDate}
                    onChange={(e) => setPremiumOwner({...premiumOwner, newPremiumStartDate: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Premium End Date</label>
                  <input
                    type="date"
                    className="block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-600 focus:border-primary-600"
                    value={premiumOwner.newPremiumEndDate}
                    onChange={(e) => setPremiumOwner({...premiumOwner, newPremiumEndDate: e.target.value})}
                  />
                </div>
              </div>
            </div>
            
            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount Paid</label>
                <input
                  type="number"
                  className="block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-600 focus:border-primary-600"
                  value={premiumOwner.amount_paid}
                  onChange={(e) => setPremiumOwner({...premiumOwner, amount_paid: e.target.value})}
                  placeholder="Enter amount"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                <select
                  className="block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-600 focus:border-primary-600"
                  value={premiumOwner.payment_method}
                  onChange={(e) => setPremiumOwner({...premiumOwner, payment_method: e.target.value})}
                >
                  <option value="UPI">UPI</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Credit Card">Credit Card</option>
                  <option value="Cash">Cash</option>
                </select>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                className="bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-lg"
                onClick={handleSavePremium}
              >
                Save Changes
              </button>
              <button
                type="button"
                className="ml-2 bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium py-2 px-4 rounded-lg"
                onClick={() => setIsPremiumModalOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Employee Modal */}
      {isEmployeeModalOpen && (
        <div className="fixed inset-0 overflow-y-auto z-50">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={closeEmployeeModal}></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-xl leading-6 font-bold text-gray-900">
                        Employees at {selectedSalonName}
                      </h3>
                      <button 
                        onClick={closeEmployeeModal}
                        className="text-gray-400 hover:text-gray-500"
                      >
                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    
                    <div>
                      {selectedSalonEmployees.length > 0 ? (
                        selectedSalonEmployees.map((employee) => (
                          <div key={employee.id} className="mb-4 p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                            <div className="flex items-center mb-2">
                              <div className="flex-shrink-0 h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                                <span className="text-primary-600 font-medium">{employee.name.charAt(0)}</span>
                              </div>
                              <div className="ml-3">
                                <div className="text-sm font-medium text-gray-900">{employee.name}</div>
                                <div className="text-xs text-gray-500">{employee.position}</div>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div>
                                <span className="text-gray-500">Join Date:</span>
                                <div className="font-medium">{employee.joinDate}</div>
                                <div className="text-xs text-gray-500">{calculateDuration(employee.joinDate)} ago</div>
                              </div>
                              <div>
                                <span className="text-gray-500">Contact:</span>
                                <div className="font-medium">{employee.contact}</div>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-4 text-gray-500">
                          No employees found for this salon.
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-6 pt-4 border-t border-gray-200">
                      <p className="text-sm text-gray-500">
                        Showing {selectedSalonEmployees.length} employees currently working at {selectedSalonName}.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:w-auto sm:text-sm"
                  onClick={closeEmployeeModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SalonOwners;









































